import React, { useState, useEffect } from 'react'
import { 
  DocumentChartBarIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  FunnelIcon
} from '@heroicons/react/24/outline'
import { apiClient } from '../../services/apiClient'

interface FundFlow {
  id: string
  user_name: string
  user_email: string
  operator_name?: string
  type: string
  amount: number
  balance_after: number
  description: string
  status: string
  created_at: string
}

interface AuditReport {
  report_info: {
    type: string
    period: string
    generated_at: string
    generated_by: string
  }
  summary: {
    total_users: number
    total_operators: number
    total_deposits: number
    total_withdrawals: number
    total_trading_volume: number
    total_commission: number
    net_fund_flow: number
  }
  fund_flows: {
    deposits: {
      count: number
      total_amount: number
      completed_amount: number
      pending_amount: number
    }
    withdrawals: {
      count: number
      total_amount: number
      completed_amount: number
      pending_amount: number
      rejected_amount: number
    }
    trading: {
      count: number
      total_volume: number
      total_profit: number
      total_loss: number
      commission_generated: number
    }
  }
  reconciliation: {
    user_balances: {
      total_real_balance: number
      total_gift_balance: number
      total_frozen_balance: number
      balance_consistency: boolean
    }
    operator_balances: {
      total_balance: number
      total_commission: number
      balance_consistency: boolean
    }
  }
}

interface ReconciliationStatus {
  user_balances: {
    account_total: number
    flow_total: number
    difference: number
    is_balanced: boolean
  }
  operator_balances: {
    account_total: number
    commission_total: number
    difference: number
    is_balanced: boolean
  }
  overall_status: {
    is_balanced: boolean
    last_checked: string
  }
}

export default function AuditPage() {
  const [activeTab, setActiveTab] = useState<'flows' | 'reports' | 'reconciliation'>('flows')
  
  // Fund flows state
  const [fundFlows, setFundFlows] = useState<FundFlow[]>([])
  const [flowsLoading, setFlowsLoading] = useState(false)
  const [flowsError, setFlowsError] = useState<string | null>(null)
  const [flowsSummary, setFlowsSummary] = useState({
    total_inflow: 0,
    total_outflow: 0,
    net_flow: 0,
    transaction_count: 0
  })
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  
  // Reports state
  const [auditReport, setAuditReport] = useState<AuditReport | null>(null)
  const [reportLoading, setReportLoading] = useState(false)
  const [reportType, setReportType] = useState<'daily' | 'weekly' | 'monthly' | 'custom'>('daily')
  const [reportDateFrom, setReportDateFrom] = useState('')
  const [reportDateTo, setReportDateTo] = useState('')
  const [includeDetails, setIncludeDetails] = useState(false)
  
  // Reconciliation state
  const [reconciliationStatus, setReconciliationStatus] = useState<ReconciliationStatus | null>(null)
  const [reconciliationLoading, setReconciliationLoading] = useState(false)

  useEffect(() => {
    if (activeTab === 'flows') {
      fetchFundFlows()
    } else if (activeTab === 'reconciliation') {
      fetchReconciliationStatus()
    }
  }, [activeTab, currentPage, searchTerm, typeFilter, statusFilter, dateFrom, dateTo])

  const fetchFundFlows = async () => {
    try {
      setFlowsLoading(true)
      setFlowsError(null)
      
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
        ...(searchTerm && { search: searchTerm }),
        ...(typeFilter !== 'all' && { type: typeFilter }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo && { date_to: dateTo }),
        sort_by: 'created_at',
        sort_order: 'desc'
      })

      const response = await apiClient.getFundFlows(params)
      
      if (response.success && response.data) {
        setFundFlows(response.data.fund_flows)
        setFlowsSummary(response.data.summary)
      } else {
        setFlowsError(response.message || '获取资金流水失败')
      }
    } catch (err) {
      setFlowsError('网络错误，请稍后重试')
      console.error('Fetch fund flows error:', err)
    } finally {
      setFlowsLoading(false)
    }
  }

  const generateAuditReport = async () => {
    if (!reportDateFrom || !reportDateTo) {
      alert('请选择报表日期范围')
      return
    }

    try {
      setReportLoading(true)
      
      const response = await apiClient.generateAuditReport({
        report_type: reportType,
        date_from: reportDateFrom,
        date_to: reportDateTo,
        include_details: includeDetails
      })
      
      if (response.success && response.data) {
        setAuditReport(response.data)
      } else {
        alert('生成审计报表失败: ' + response.message)
      }
    } catch (err) {
      alert('生成审计报表失败，请稍后重试')
      console.error('Generate audit report error:', err)
    } finally {
      setReportLoading(false)
    }
  }

  const fetchReconciliationStatus = async () => {
    try {
      setReconciliationLoading(true)
      
      const response = await apiClient.getReconciliationStatus()
      
      if (response.success && response.data) {
        setReconciliationStatus(response.data.reconciliation_status)
      } else {
        console.error('获取对账状态失败:', response.message)
      }
    } catch (err) {
      console.error('Fetch reconciliation status error:', err)
    } finally {
      setReconciliationLoading(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY',
      minimumFractionDigits: 2
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('zh-CN')
  }

  const getTypeLabel = (type: string) => {
    const typeLabels: Record<string, string> = {
      deposit: '充值',
      deposit_completed: '充值完成',
      withdraw: '提现',
      withdraw_completed: '提现完成',
      withdraw_rejected: '提现拒绝',
      trading_profit: '交易盈利',
      trading_loss: '交易亏损',
      commission: '佣金',
      bonus: '奖金',
      penalty: '罚金'
    }
    return typeLabels[type] || type
  }

  return (
    <div className="page-content">
      {/* 页面标题 */}
      <div className="mb-8">
        <h1 className="page-title">资金审计</h1>
        <p className="page-subtitle">资金流水查询、审计报表和对账管理</p>
      </div>

      {/* Tab 导航 */}
      <div className="mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('flows')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'flows'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <CurrencyDollarIcon className="h-5 w-5 inline mr-2" />
            资金流水
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'reports'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <DocumentChartBarIcon className="h-5 w-5 inline mr-2" />
            审计报表
          </button>
          <button
            onClick={() => setActiveTab('reconciliation')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'reconciliation'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <CheckCircleIcon className="h-5 w-5 inline mr-2" />
            资金对账
          </button>
        </nav>
      </div>

      {/* 资金流水 Tab */}
      {activeTab === 'flows' && (
        <div className="space-y-6">
          {/* 汇总卡片 */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="card">
              <div className="card-body">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <CurrencyDollarIcon className="w-5 h-5 text-green-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">总流入</p>
                    <p className="text-lg font-semibold text-green-600">
                      {formatCurrency(flowsSummary?.total_inflow || 0)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                      <CurrencyDollarIcon className="w-5 h-5 text-red-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">总流出</p>
                    <p className="text-lg font-semibold text-red-600">
                      {formatCurrency(flowsSummary?.total_outflow || 0)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <CurrencyDollarIcon className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">净流量</p>
                    <p className={`text-lg font-semibold ${(flowsSummary?.net_flow || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(flowsSummary?.net_flow || 0)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                      <DocumentChartBarIcon className="w-5 h-5 text-gray-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">交易笔数</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {(flowsSummary?.transaction_count || 0).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 筛选器 */}
          <div className="card">
            <div className="card-body">
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">搜索</label>
                  <div className="relative">
                    <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="用户名或描述"
                      className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">类型</label>
                  <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="all">全部类型</option>
                    <option value="deposit">充值</option>
                    <option value="withdraw">提现</option>
                    <option value="trading_profit">交易盈利</option>
                    <option value="trading_loss">交易亏损</option>
                    <option value="commission">佣金</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">状态</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="all">全部状态</option>
                    <option value="completed">已完成</option>
                    <option value="pending">待处理</option>
                    <option value="failed">失败</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">开始日期</label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">结束日期</label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>

                <div className="flex items-end">
                  <button
                    onClick={fetchFundFlows}
                    className="w-full btn btn-secondary"
                  >
                    <FunnelIcon className="h-4 w-4 mr-2" />
                    筛选
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* 资金流水列表 */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">资金流水记录</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">用户</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">类型</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">金额</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">余额</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">描述</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">时间</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {flowsLoading ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                        加载中...
                      </td>
                    </tr>
                  ) : !fundFlows || fundFlows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                        暂无数据
                      </td>
                    </tr>
                  ) : (
                    fundFlows.map((flow) => (
                      <tr key={flow.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{flow.user_name}</div>
                            <div className="text-sm text-gray-500">{flow.user_email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {getTypeLabel(flow.type)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`text-sm font-medium ${flow.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {flow.amount >= 0 ? '+' : ''}{formatCurrency(flow.amount)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(flow.balance_after)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                          {flow.description}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            flow.status === 'completed' ? 'bg-green-100 text-green-800' :
                            flow.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {flow.status === 'completed' ? '已完成' :
                             flow.status === 'pending' ? '待处理' : '失败'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(flow.created_at)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* 审计报表 Tab */}
      {activeTab === 'reports' && (
        <div className="space-y-6">
          {/* 报表生成 */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">生成审计报表</h3>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">报表类型</label>
                  <select
                    value={reportType}
                    onChange={(e) => setReportType(e.target.value as any)}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="daily">日报</option>
                    <option value="weekly">周报</option>
                    <option value="monthly">月报</option>
                    <option value="custom">自定义</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">开始日期</label>
                  <input
                    type="date"
                    value={reportDateFrom}
                    onChange={(e) => setReportDateFrom(e.target.value)}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">结束日期</label>
                  <input
                    type="date"
                    value={reportDateTo}
                    onChange={(e) => setReportDateTo(e.target.value)}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    id="include-details"
                    type="checkbox"
                    checked={includeDetails}
                    onChange={(e) => setIncludeDetails(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="include-details" className="ml-2 block text-sm text-gray-900">
                    包含详细数据
                  </label>
                </div>

                <div className="flex items-end">
                  <button
                    onClick={generateAuditReport}
                    disabled={reportLoading}
                    className="w-full btn btn-primary"
                  >
                    <DocumentChartBarIcon className="h-4 w-4 mr-2" />
                    {reportLoading ? '生成中...' : '生成报表'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* 报表结果 */}
          {auditReport && (
            <div className="space-y-6">
              {/* 报表信息 */}
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">报表信息</h3>
                </div>
                <div className="card-body">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">报表类型</p>
                      <p className="font-medium">{auditReport.report_info.type}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">统计周期</p>
                      <p className="font-medium">{auditReport.report_info.period}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">生成时间</p>
                      <p className="font-medium">{formatDate(auditReport.report_info.generated_at)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">生成人</p>
                      <p className="font-medium">{auditReport.report_info.generated_by}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 汇总统计 */}
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">汇总统计</h3>
                </div>
                <div className="card-body">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600">{auditReport.summary.total_users}</p>
                      <p className="text-sm text-gray-500">新增用户</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">{formatCurrency(auditReport.summary.total_deposits)}</p>
                      <p className="text-sm text-gray-500">总充值</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-red-600">{formatCurrency(auditReport.summary.total_withdrawals)}</p>
                      <p className="text-sm text-gray-500">总提现</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-purple-600">{formatCurrency(auditReport.summary.total_commission)}</p>
                      <p className="text-sm text-gray-500">总佣金</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 对账状态 */}
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">对账状态</h3>
                </div>
                <div className="card-body">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-center">
                      {auditReport.reconciliation.user_balances.balance_consistency ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                      ) : (
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2" />
                      )}
                      <span className="font-medium">用户余额一致性</span>
                      <span className={`ml-2 text-sm ${auditReport.reconciliation.user_balances.balance_consistency ? 'text-green-600' : 'text-red-600'}`}>
                        {auditReport.reconciliation.user_balances.balance_consistency ? '正常' : '异常'}
                      </span>
                    </div>
                    <div className="flex items-center">
                      {auditReport.reconciliation.operator_balances.balance_consistency ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                      ) : (
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2" />
                      )}
                      <span className="font-medium">运营商余额一致性</span>
                      <span className={`ml-2 text-sm ${auditReport.reconciliation.operator_balances.balance_consistency ? 'text-green-600' : 'text-red-600'}`}>
                        {auditReport.reconciliation.operator_balances.balance_consistency ? '正常' : '异常'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 资金对账 Tab */}
      {activeTab === 'reconciliation' && (
        <div className="space-y-6">
          {reconciliationLoading ? (
            <div className="card">
              <div className="card-body text-center py-12">
                <div className="text-gray-500">加载对账状态中...</div>
              </div>
            </div>
          ) : reconciliationStatus ? (
            <>
              {/* 总体状态 */}
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">对账总体状态</h3>
                </div>
                <div className="card-body">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      {reconciliationStatus.overall_status.is_balanced ? (
                        <CheckCircleIcon className="h-8 w-8 text-green-500 mr-3" />
                      ) : (
                        <ExclamationTriangleIcon className="h-8 w-8 text-red-500 mr-3" />
                      )}
                      <div>
                        <p className="text-lg font-semibold">
                          {reconciliationStatus.overall_status.is_balanced ? '账目平衡' : '存在差异'}
                        </p>
                        <p className="text-sm text-gray-500">
                          最后检查时间: {formatDate(reconciliationStatus.overall_status.last_checked)}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={fetchReconciliationStatus}
                      className="btn btn-secondary"
                    >
                      刷新状态
                    </button>
                  </div>
                </div>
              </div>

              {/* 用户余额对账 */}
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">用户余额对账</h3>
                </div>
                <div className="card-body">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600">
                        {formatCurrency(reconciliationStatus.user_balances.account_total)}
                      </p>
                      <p className="text-sm text-gray-500">账户余额总计</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">
                        {formatCurrency(reconciliationStatus.user_balances.flow_total)}
                      </p>
                      <p className="text-sm text-gray-500">流水累计总额</p>
                    </div>
                    <div className="text-center">
                      <p className={`text-2xl font-bold ${Math.abs(reconciliationStatus.user_balances.difference) < 0.01 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(reconciliationStatus.user_balances.difference)}
                      </p>
                      <p className="text-sm text-gray-500">差异金额</p>
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      reconciliationStatus.user_balances.is_balanced 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {reconciliationStatus.user_balances.is_balanced ? '余额一致' : '存在差异'}
                    </span>
                  </div>
                </div>
              </div>

              {/* 运营商余额对账 */}
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">运营商余额对账</h3>
                </div>
                <div className="card-body">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600">
                        {formatCurrency(reconciliationStatus.operator_balances.account_total)}
                      </p>
                      <p className="text-sm text-gray-500">账户余额总计</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">
                        {formatCurrency(reconciliationStatus.operator_balances.commission_total)}
                      </p>
                      <p className="text-sm text-gray-500">佣金流水总额</p>
                    </div>
                    <div className="text-center">
                      <p className={`text-2xl font-bold ${Math.abs(reconciliationStatus.operator_balances.difference) < 0.01 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(reconciliationStatus.operator_balances.difference)}
                      </p>
                      <p className="text-sm text-gray-500">差异金额</p>
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      reconciliationStatus.operator_balances.is_balanced 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {reconciliationStatus.operator_balances.is_balanced ? '余额一致' : '存在差异'}
                    </span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="card">
              <div className="card-body text-center py-12">
                <div className="text-gray-500">无法获取对账状态</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}